/**
 * Postal code regex patterns per country (ISO 3166-1 alpha-2 codes)
 * Source: postal_code_regex_per_country.csv
 */

export interface PostalCodePattern {
  countryCode: string;
  countryName: string;
  regex: RegExp;
  example: string;
  nationalTerm: string;
}

export const postalCodePatterns: Record<string, PostalCodePattern> = {
  AT: {
    countryCode: "AT",
    countryName: "Austria",
    regex: /^\d{4}$/,
    example: "1010",
    nationalTerm: "PLZ",
  },
  BE: {
    countryCode: "BE",
    countryName: "Belgium",
    regex: /^\d{4}$/,
    example: "1049",
    nationalTerm: "Code postal",
  },
  BG: {
    countryCode: "BG",
    countryName: "Bulgaria",
    regex: /^\d{4}$/,
    example: "1000",
    nationalTerm: "Пощенски код",
  },
  CH: {
    countryCode: "CH",
    countryName: "Switzerland",
    regex: /^[1-9]\d{3}$/,
    example: "8050",
    nationalTerm: "PLZ / Code postal",
  },
  CY: {
    countryCode: "CY",
    countryName: "Cyprus",
    regex: /^[1-9]\d{3}$/,
    example: "4999",
    nationalTerm: "Postcode",
  },
  CZ: {
    countryCode: "CZ",
    countryName: "Czech Republic",
    regex: /^[1-7][0-9]{2} [0-9]{2}$|^[1-7][0-9]{4}$/,
    example: "160 00, 16000",
    nationalTerm: "PSČ",
  },
  DE: {
    countryCode: "DE",
    countryName: "Germany",
    regex: /^\d{5}$/,
    example: "60320",
    nationalTerm: "PLZ",
  },
  DK: {
    countryCode: "DK",
    countryName: "Denmark",
    regex: /^\d{4}$/,
    example: "2000",
    nationalTerm: "Postnummer",
  },
  EE: {
    countryCode: "EE",
    countryName: "Estonia",
    regex: /^\d{5}$/,
    example: "10111",
    nationalTerm: "Postiindeks",
  },
  ES: {
    countryCode: "ES",
    countryName: "Spain",
    regex: /^\d{5}$/,
    example: "28006",
    nationalTerm: "Código postal",
  },
  FI: {
    countryCode: "FI",
    countryName: "Finland",
    regex: /^\d{5}$/,
    example: "00180",
    nationalTerm: "Postinumero",
  },
  FR: {
    countryCode: "FR",
    countryName: "France",
    regex: /^\d{5}$/,
    example: "75008",
    nationalTerm: "Code postal",
  },
  GR: {
    countryCode: "GR",
    countryName: "Greece",
    regex: /^(\d{3}) \d{2}$|^\d{5}$/,
    example: "241 00, 24100",
    nationalTerm: "T.K.",
  },
  HR: {
    countryCode: "HR",
    countryName: "Croatia",
    regex: /^[1-5]\d{4}$/,
    example: "21000",
    nationalTerm: "Poštanski broj",
  },
  HU: {
    countryCode: "HU",
    countryName: "Hungary",
    regex: /^[1-9]\d{3}$/,
    example: "2310",
    nationalTerm: "Irányítószám",
  },
  IE: {
    countryCode: "IE",
    countryName: "Ireland",
    regex: /^(?:^[AC-FHKNPRTV-Y][0-9]{2}|D6W)[ -]?[0-9AC-FHKNPRTV-Y]{4}$/,
    example: "D02 AF30",
    nationalTerm: "Eircode",
  },
  IT: {
    countryCode: "IT",
    countryName: "Italy",
    regex: /^\d{5}$/,
    example: "36051",
    nationalTerm: "CAP",
  },
  LT: {
    countryCode: "LT",
    countryName: "Lithuania",
    regex: /^((LT)[-])?(\d{5})$/,
    example: "LT-01100, 01100",
    nationalTerm: "Pašto kodas",
  },
  LU: {
    countryCode: "LU",
    countryName: "Luxembourg",
    regex: /^((L)[-])?(\d{4})$/,
    example: "1019, L-2530",
    nationalTerm: "Code postal",
  },
  LV: {
    countryCode: "LV",
    countryName: "Latvia",
    regex: /^((LV)[-])?(\d{4})$/,
    example: "LV-1010, 1010",
    nationalTerm: "Pasta indekss",
  },
  MT: {
    countryCode: "MT",
    countryName: "Malta",
    regex: /^[A-Z]{3} [0-9]{4}$|^[A-Z]{2}[0-9]{2}$|^[A-Z]{2} [0-9]{2}$|^[A-Z]{3}[0-9]{4}$|^[A-Z]{3}[0-9]{2}$|^[A-Z]{3} [0-9]{2}$/,
    example: "VLT 1117, TP01, TP 01, RBT1676, QRM09, BKR 01",
    nationalTerm: "Kodiċi Postali",
  },
  NL: {
    countryCode: "NL",
    countryName: "Netherlands",
    regex: /^[1-9]\d{3} [A-Z]{2}$|^[1-9]\d{3}[A-Z]{2}$/,
    example: "1011 AC, 1011AC",
    nationalTerm: "Postcode",
  },
  PL: {
    countryCode: "PL",
    countryName: "Poland",
    regex: /^[0-9]{2}[-]([0-9]){3}$/,
    example: "87-100",
    nationalTerm: "Kod pocztowy",
  },
  PT: {
    countryCode: "PT",
    countryName: "Portugal",
    regex: /^[1-9]\d{3}((-)\d{3})$/,
    example: "1000-260",
    nationalTerm: "Código postal",
  },
  RO: {
    countryCode: "RO",
    countryName: "Romania",
    regex: /^\d{6}$/,
    example: "507085",
    nationalTerm: "Cod poştal",
  },
  SE: {
    countryCode: "SE",
    countryName: "Sweden",
    regex: /^[1-9]\d{2} \d{2}$/,
    example: "113 51",
    nationalTerm: "Postnummer",
  },
  SI: {
    countryCode: "SI",
    countryName: "Slovenia",
    regex: /^[1-9]\d{3}$/,
    example: "8341",
    nationalTerm: "Poštna številka",
  },
  SK: {
    countryCode: "SK",
    countryName: "Slovakia",
    regex: /^(\d{3} \d{2})$|^\d{5}$/,
    example: "811 01, 81101",
    nationalTerm: "PSČ",
  },
};

/**
 * Get postal code regex pattern for a country
 */
export function getPostalCodePattern(countryCode: string): PostalCodePattern | undefined {
  return postalCodePatterns[countryCode];
}

/**
 * Validate postal code for a specific country
 */
export function validatePostalCode(postalCode: string, countryCode: string): boolean {
  const pattern = getPostalCodePattern(countryCode);
  if (!pattern) {
    // If no pattern exists for country, allow any non-empty string
    return postalCode.trim().length > 0;
  }
  return pattern.regex.test(postalCode);
}

/**
 * Get postal code example for a country
 */
export function getPostalCodeExample(countryCode: string): string {
  const pattern = getPostalCodePattern(countryCode);
  return pattern?.example || "";
}

/**
 * Get national term for postal code (e.g., "PLZ" for Germany)
 */
export function getPostalCodeTerm(countryCode: string): string {
  const pattern = getPostalCodePattern(countryCode);
  return pattern?.nationalTerm || "Postleitzahl";
}
